'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getImageUrl } from '../../config/api';
import { getSectionByKey } from '../../services/cmsService';
import { handleImageError } from '../../utils/imageUtils';
import './HeroSection.css';

const defaultHeroSlide = {
  src: getImageUrl('https://firebasestorage.googleapis.com/v0/b/rangaara-c6dac.firebasestorage.app/o/cms_images%2Fcms_upload_1788286847068?alt=media&token=04ccbf2a-2db8-47a0-b7c9-aa1f8830ba08', { w: 1600, h: 700, fo: 'auto', q: 75 }),
  mobileSrc: getImageUrl('https://firebasestorage.googleapis.com/v0/b/rangaara-c6dac.firebasestorage.app/o/cms_images%2Fcms_upload_1788286847068?alt=media&token=04ccbf2a-2db8-47a0-b7c9-aa1f8830ba08', { w: 768, h: 1000, fo: 'auto', q: 75 }),
  originalSrc: 'https://firebasestorage.googleapis.com/v0/b/rangaara-c6dac.firebasestorage.app/o/cms_images%2Fcms_upload_1788286847068?alt=media&token=04ccbf2a-2db8-47a0-b7c9-aa1f8830ba08',
  alt: 'Hero Image',
  subtitleTop: 'OUR JOURNEY BEGINS',
  title: 'WEBSITE LIVE NOW',
  subtitle: 'Crafted with passion, built for you. Explore, discover & shop your favorites.',
  ctaText: 'USE CODE: LUNCH20',
  link: '/shop',
  objectPosition: 'center 15%'
};

const HeroSection = ({ initialData }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [slides, setSlides] = useState([defaultHeroSlide]);
  const [portraitSlides, setPortraitSlides] = useState({});
  const intervalRef = useRef(null);

  const fallbackSlides = React.useMemo(() => [
    { 
      src: '/img/Approved_Drzya-Desktop-14.webp',
      mobileSrc: '/img/Approved_Drzya-Desktop-14.webp',
      alt: 'Royal Heritage Collection', 
      subtitleTop: 'RANGAARA AUTUMN EDIT',
      title: 'THE ROYAL HERITAGE',
      subtitle: 'Experience the regal elegance of handcrafted Indian couture',
      ctaText: 'EXPLORE COLLECTION',
      link: '/shop',
      objectPosition: 'center top'
    },
    { 
      src: '/img/Approved_Drzya-Desktop-14.webp',
      mobileSrc: '/img/Approved_Drzya-Desktop-14.webp',
      alt: 'Festive Splendor', 
      subtitleTop: 'THE SHADI COLLECTION',
      title: 'FESTIVE SPLENDOR',
      subtitle: 'Premium ethnic wear designed for your special celebrations',
      ctaText: 'DISCOVER NOW',
      link: '/shop?collection=Nur',
      objectPosition: 'center top'
    },
    { 
      src: '/img/Drzya-Desktop-1_2.webp',
      mobileSrc: '/img/Drzya-Desktop-1_2.webp',
      alt: 'Modern Craftsmanship', 
      subtitleTop: 'ARTISANAL CHRONICLES',
      title: 'THE ART OF WEAVING',
      subtitle: 'Where traditional artisan techniques meet contemporary design',
      ctaText: 'OUR HERITAGE',
      link: '/about',
      objectPosition: 'center top'
    },
    { 
      src: '/img/Drzya-Desktop-11.webp',
      mobileSrc: '/img/Drzya-Desktop-11.webp',
      alt: 'Luxury Edit', 
      subtitleTop: 'VELVET & TISSUE SPECIALS',
      title: 'THE LUXURY EDIT',
      subtitle: 'Hand-selected silk, tissue and embroidered velvet silhouettes',
      ctaText: 'SHOP EXCLUSIVES',
      link: '/shop?filter=luxury',
      objectPosition: 'center top'
    }
  ], []);

  useEffect(() => {
    const fetchHero = async () => {
      try {
        if (!initialData) setLoading(true);
        const sectionData = initialData || await getSectionByKey('hero_section');
        const sectionSlides = sectionData?.content?.slides || [];
        const updatedAt = sectionData?.updatedAt;
        
        if (sectionSlides.length > 0) {
          const mappedSlides = sectionSlides
            .filter(s => s.isActive !== false)
            .map(s => {
              // Request desktop slide image with standard banner transformations (1600x700, auto-focus cropping)
              const desktopPath = getImageUrl(
                s.image || s.desktopImage || s.src,
                { w: 1600, h: 700, fo: 'auto', q: 85 },
                updatedAt
              );
              // Request mobile slide image with mobile banner transformations (768x1000, portrait auto-focus cropping)
              const mobilePath = s.mobileImage 
                ? getImageUrl(s.mobileImage, { w: 768, h: 1000, fo: 'auto', q: 85 }, updatedAt) 
                : getImageUrl(s.image || s.desktopImage || s.src, { w: 768, h: 1000, fo: 'auto', q: 85 }, updatedAt);

              const rawOrigin = s.image || s.desktopImage || s.src;
              const rawSrc = typeof rawOrigin === 'string' ? rawOrigin : (rawOrigin?.url || '');

              return {
                src: desktopPath,
                mobileSrc: mobilePath,
                originalSrc: rawSrc,
                alt: s.heading || s.alt || 'Hero Image',
                subtitleTop: s.subheading || s.subtitleTop,
                title: s.heading || s.title,
                subtitle: s.description || s.subtitle,
                ctaText: s.primaryBtnText || s.ctaText,
                link: s.primaryBtnUrl || s.link,
                objectPosition: s.objectPosition || s.layoutConfig?.objectPosition || 'center center'
              };
            });
          setSlides(mappedSlides.length > 0 ? mappedSlides : fallbackSlides);
        } else {
          setSlides(fallbackSlides);
        }
      } catch (err) {
        console.error('Failed to fetch hero section from Firestore', err);
        setSlides(fallbackSlides);
      } finally {
        setLoading(false);
      }
    };
    fetchHero();
  }, [fallbackSlides]);

  const nextSlide = useCallback(() => {
    if (slides.length === 0) return;
    setCurrentSlideIndex((prevIndex) => (prevIndex + 1) % slides.length);
  }, [slides.length]);

  const goToSlide = (index) => {
    if (index === currentSlideIndex) return;
    setCurrentSlideIndex(index);
  };

  useEffect(() => {
    if (slides.length <= 1) return;
    intervalRef.current = setInterval(() => {
      nextSlide();
    }, 6000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [nextSlide, slides.length]);

  if (loading && slides.length === 0) {
    return (
      <section className="hero-section hero-loading-skeleton">
        <div className="hero-slider" style={{ background: '#f5f0eb' }}></div>
      </section>
    );
  }

  if (slides.length === 0) return null;

  return (
    <section className="hero-section">
      <div className="hero-slider">
        {slides.map((slide, index) => (
          <div 
            key={index}
            className={`hero-slide ${index === currentSlideIndex ? 'active' : ''}`}
            style={{ backgroundColor: portraitSlides[index] ? '#FAF6F2' : '#111827' }}
          >
            <div className="hero-dark-overlay"></div>
            <picture className="hero-picture">
              <source media="(max-width: 768px)" srcSet={slide.mobileSrc || slide.src} />
              <img 
                src={slide.src} 
                data-original-src={slide.originalSrc}
                alt={slide.alt} 
                onLoad={(e) => {
                  const { naturalWidth, naturalHeight } = e.target;
                  if (naturalHeight > naturalWidth) {
                    setPortraitSlides(prev => ({ ...prev, [index]: true }));
                  }
                }}
                onError={(e) => handleImageError(e)}
                style={{ 
                  objectPosition: portraitSlides[index] ? 'center 15%' : (slide.objectPosition || 'center center'),
                  objectFit: 'cover',
                  transform: portraitSlides[index] ? 'scale(0.92)' : 'none',
                  borderRadius: portraitSlides[index] ? '12px' : '0'
                }}
                loading={index === 0 ? "eager" : "lazy"}
                decoding={index === 0 ? "sync" : "async"}
                fetchPriority={index === 0 ? "high" : "auto"}
              />
            </picture>
            <div className="hero-content">
              {slide.subtitleTop && <span className="hero-subtitle-top">{slide.subtitleTop}</span>}
              {slide.title && <h1 className="hero-title">{slide.title}</h1>}
              {slide.subtitle && <p className="hero-subtitle">{slide.subtitle}</p>}
              {slide.ctaText && (
                <a href={slide.link || '/shop'} className="hero-cta-btn">
                  {slide.ctaText} <i className="fa-solid fa-arrow-right"></i>
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
      {slides.length > 1 && (
        <div className="hero-dots" aria-label="Hero slider pagination">
          {slides.map((_, index) => (
            <button 
              key={index}
              className={`hero-dot-btn ${index === currentSlideIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default HeroSection;